import { StudioAuditTimelineAction, StudioSettingsSection, UserRole } from "../enums";
import { StudioSettingsSectionLabels } from "../labels";
import {
  IPlannyAccessLogItem,
  IPlannyAccessMonthGroup,
} from "../models/plannyAccessLogItemModel";

/**
 * Formatting for the studio access log — the owner's "Моят профил" → "Достъп от Planny"
 * tab and the preview line in the global admin's confirmation dialog.
 */

/** Mirrors StudioAuditLogService.MaxStudioAccessLogEntries — the API returns at most this many. */
export const PLANNY_ACCESS_LOG_MAX_ENTRIES = 200;

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const MONTH_NAMES = [
  "Януари",
  "Февруари",
  "Март",
  "Април",
  "Май",
  "Юни",
  "Юли",
  "Август",
  "Септември",
  "Октомври",
  "Ноември",
  "Декември",
];

/** The API sends UTC without an offset; read it as UTC, not as local time. */
const parseUtcTimestamp = (timestamp: string): Date => {
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(timestamp);
  return new Date(hasOffset ? timestamp : `${timestamp}Z`);
};

/** Calendar parts of a moment in the given time zone (the browser's when omitted). */
const getZonedParts = (date: Date, timeZone?: string) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  return {
    year: part("year"),
    month: part("month"),
    day: part("day"),
    hour: part("hour"),
    minute: part("minute"),
  };
};

export const getRelativeAccessLabel = (timestamp: string, now: Date = new Date()): string => {
  const elapsed = now.getTime() - parseUtcTimestamp(timestamp).getTime();

  if (elapsed < MINUTE_MS) {
    return "току-що";
  }
  if (elapsed < HOUR_MS) {
    return `преди ${Math.floor(elapsed / MINUTE_MS)} мин.`;
  }
  if (elapsed < DAY_MS) {
    return `преди ${Math.floor(elapsed / HOUR_MS)} ч.`;
  }
  if (elapsed < 2 * DAY_MS) {
    return "вчера";
  }

  const days = Math.floor(elapsed / DAY_MS);

  if (days < 30) {
    return `преди ${days} дни`;
  }
  if (days < 365) {
    return `преди ${Math.floor(days / 30)} мес.`;
  }
  return "преди повече от година";
};

export const formatAccessDateTime = (
  timestamp: string,
  timeZone?: string,
): { date: string; time: string } => {
  const { year, month, day, hour, minute } = getZonedParts(parseUtcTimestamp(timestamp), timeZone);
  return { date: `${day}.${month}.${year}`, time: `${hour}:${minute}` };
};

/** Splits a newest-first log into month sections, keeping the order within each. */
export const groupAccessLogByMonth = (
  items: IPlannyAccessLogItem[],
  timeZone?: string,
): IPlannyAccessMonthGroup[] => {
  const groups: IPlannyAccessMonthGroup[] = [];

  items.forEach((item) => {
    const { year, month } = getZonedParts(parseUtcTimestamp(item.timestamp), timeZone);
    const key = `${year}-${month}`;
    const last = groups[groups.length - 1];

    if (last?.key === key) {
      last.items.push(item);
      return;
    }

    groups.push({ key, label: `${MONTH_NAMES[Number(month) - 1]} ${year}`, items: [item] });
  });

  return groups;
};

/** Who opened the studio, as the owner reads it. */
export const getAccessActorLabel = (actorDisplayName: string | null | undefined): string => {
  const name = actorDisplayName?.trim();
  return name ? `${name} (екип на Planny)` : "Екипът на Planny";
};

/**
 * What the admin did, as the owner reads it: "Отвори обекта", "Промени работното време",
 * "Излезе от обекта", "Влезе в профила на Мария Петрова".
 */
export const getAccessActionLabel = (
  action: StudioAuditTimelineAction,
  section: StudioSettingsSection | null | undefined,
  profileName?: string | null,
): string => {
  if (action === StudioAuditTimelineAction.GlobalAdminLeft) {
    return "Излезе от обекта";
  }

  if (action === StudioAuditTimelineAction.GlobalAdminOpenedProfile) {
    const name = profileName?.trim();
    return name ? `Влезе в профила на ${name}` : "Влезе в профил на потребител";
  }

  if (action !== StudioAuditTimelineAction.GlobalAdminEditedSettings) {
    return "Отвори обекта";
  }

  const sectionLabel = section ? StudioSettingsSectionLabels[section] : undefined;
  return `Промени ${sectionLabel ?? "настройките"}`;
};

/**
 * Who sees the "Достъп от Planny" tab: the studio's owner and its managers, nobody else.
 * Mirrors [RolesAuthorize(Tenant, Employee_Manager)] on StudioAuditLog/GetMyStudioAccessLog.
 */
export const canSeePlannyAccessLog = (role: UserRole | undefined): boolean =>
  role === UserRole.Tenant || role === UserRole.Employee_Manager;
